import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BringzzIcon, BringzzText } from "@bringzz/components";
import { ShimmerText } from './Shimmer';
import MapInitializer from '../../../Map/components/MapInitializer';

const LoadingOpeningHours = () => (
  <div className="space-y-2">
    {[1, 2, 3, 4, 5, 6, 7].map((day) => (
      <div key={day} className="flex justify-between items-center p-2 border-b">
        <ShimmerText width="w-20" />
        <ShimmerText width="w-32" />
      </div>
    ))}
  </div>
);

const InfoTab = ({ loading, error, pharmacy, opening_hours, night_duties, isOwner }) => {
  const { t } = useTranslation('pharmacyProfile');
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  
  const daysOfWeek = [
    t('days.monday'), t('days.tuesday'), t('days.wednesday'), t('days.thursday'), 
    t('days.friday'), t('days.saturday'), t('days.sunday')
  ];

  const logMapEvent = (event, data) => {
    console.log(`Map Event: ${event}`, data);
  };

  const { initializeMap } = MapInitializer({
    onMapCreated: (newMap) => {
      setMap(newMap);
      setMapInitialized(true);
    },
    logMapEvent
  });

  useEffect(() => {
    if (!pharmacy?.latitude || !pharmacy?.longitude || !mapRef.current || mapInitialized) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    
    const checkAndInitialize = () => {
      attempts++;
      
      if (window.mapkit) {
        console.log('✅ Mapkit loaded, initializing map...');
        const initialLocation = {
          latitude: parseFloat(pharmacy.latitude),
          longitude: parseFloat(pharmacy.longitude)
        };
        initializeMap(mapRef, initialLocation);
      } else if (attempts < maxAttempts) {
        console.log(`⏳ Waiting for mapkit to load... (attempt ${attempts}/${maxAttempts})`);
        setTimeout(checkAndInitialize, 100);
      } else {
        console.error('❌ Mapkit failed to load after 5 seconds.');
        console.log('Debug info:', {
          scriptInDOM: !!document.querySelector('script[src*="mapkit.js"]'),
          windowMapKit: !!window.mapkit,
          protocol: window.location.protocol
        });
      }
    };
    
    checkAndInitialize();
  }, [pharmacy?.latitude, pharmacy?.longitude, mapInitialized, initializeMap, logMapEvent]);

  useEffect(() => {
    if (map && pharmacy?.latitude && pharmacy?.longitude) {
      const coordinate = new window.mapkit.Coordinate(
        parseFloat(pharmacy.latitude),
        parseFloat(pharmacy.longitude)
      );
      
      const annotation = new window.mapkit.MarkerAnnotation(coordinate, {
        title: pharmacy.name || 'Pharmacy',
        subtitle: pharmacy.email || '',
        color: '#007AFF'
      });
      
      map.addAnnotation(annotation);      
      const region = new window.mapkit.CoordinateRegion(
        coordinate,
        new window.mapkit.CoordinateSpan(0.005, 0.005)
      );
      map.region = region;
    }
  }, [map, pharmacy]);

  if (loading) {
    return (
      <div className="p-4 space-y-6">
        <div className="space-y-4">
          <ShimmerText width="w-1/3" height="h-6" />
          <LoadingOpeningHours />
        </div>

        <div className="space-y-4">
          <ShimmerText width="w-1/3" height="h-6" />
          <ShimmerText width="w-2/3" />
        </div>

        <div className="space-y-4">
          <ShimmerText width="w-1/3" height="h-6" />
          <div className="space-y-2">
            <ShimmerText width="w-1/2" />
            <ShimmerText width="w-1/2" />
            <ShimmerText width="w-1/2" />
          </div>
          <div className="h-96 w-full mt-4 rounded-lg bg-gray-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center text-red-500">
        <BringzzText tag="p">{t('tabs.info.failed_to_load')}</BringzzText>
      </div>
    );
  }

  const formatOpeningHours = (hours) => {
    if (!hours || hours.length === 0) return null;
    
    const dayGroups = {};
    hours.forEach(entry => {
      if (!dayGroups[entry.day]) {
        dayGroups[entry.day] = { isOpen: entry.isOpen, intervals: [] };
      }
      
      if (entry.isOpen === 1) {
        try {
          const starts = JSON.parse(entry.start);
          const ends = JSON.parse(entry.end);
          for (let i = 0; i < starts.length; i++) {
            dayGroups[entry.day].intervals.push(`${starts[i]} - ${ends[i]}`);
          }
        } catch (e) {
          dayGroups[entry.day].intervals.push(`${entry.start} - ${entry.end}`);
        }
      }
    });
    
    return dayGroups;
  };

  const openingHoursData = formatOpeningHours(opening_hours);

  return (
    <div className="p-4 space-y-6 pb-96 md:pb-4">
      {openingHoursData && (
        <div>
          <BringzzText tag="h3" className="font-bold text-lg mb-4">
            {t('tabs.info.opening_hours')}
          </BringzzText>
          <div className="space-y-2">
            {daysOfWeek.map((dayName, index) => {
              const dayData = openingHoursData[index + 1];
              return (
                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                  <BringzzText tag="span" className="font-medium">{dayName}</BringzzText>
                  <BringzzText tag="span" className="text-sm">
                    {dayData?.isOpen === 2 ? t('tabs.info.closed') : 
                     dayData?.intervals?.length > 0 ? dayData.intervals.join(', ') : t('tabs.info.closed')}
                  </BringzzText>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <BringzzText tag="h3" className="font-bold text-lg mb-4">
          {t('tabs.info.contact_information')}
        </BringzzText>
        <div className="space-y-2">
          {pharmacy.email && (
            <div className="flex items-center space-x-2">
              <BringzzIcon icon="IconMail" size="16" folder="LineIcons" className="text-gray-500" />
              <BringzzText tag="p">{pharmacy.email}</BringzzText>
            </div>
          )}
          {pharmacy.telefon && (
            <div className="flex items-center space-x-2">
              <BringzzIcon icon="IconPhone" size="16" folder="LineIcons" className="text-gray-500" />
              <BringzzText tag="p">{pharmacy.telefon}</BringzzText>
            </div>
          )}
          {pharmacy.fax && (
            <div className="flex items-center space-x-2">
              <BringzzIcon icon="IconPrinter" size="16" folder="LineIcons" className="text-gray-500" />
              <BringzzText tag="p">{pharmacy.fax}</BringzzText>
            </div>
          )}
        </div>
      </div>

      {pharmacy.services && pharmacy.services.length > 0 && (
        <div className="mb-32 md:mb-6">
          <BringzzText tag="h3" className="font-bold text-lg mb-4">
            {t('tabs.info.services')}
          </BringzzText>
          <div className="space-y-2">
            {pharmacy.services.map((service) => (
              <div key={service.id} className="flex items-center space-x-2">
                <BringzzIcon icon="IconCheckCircle" size="16" folder="LineIcons" className="text-green-500" />
                <BringzzText tag="p">{service.label}</BringzzText>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {pharmacy.latitude && pharmacy.longitude && (
        <div>
          <BringzzText tag="h3" className="font-bold text-lg mb-4">
            {t('tabs.info.location')}
          </BringzzText>
          <div 
            ref={mapRef}
            className="h-64 md:h-96 w-full rounded-lg overflow-hidden border"
          />
        </div>
      )}
    </div>
  );
};

export default InfoTab; 